/**
 * FilterChip — pill-shaped toggle button for filter rows.
 *
 * Used in DataTable toolbars for filter-by-category rows like
 * `All N · Yours N · Linqage N` or `All N · Running N · Done N · Failed N`.
 * Renders as a real `<button>` with `aria-pressed` so keyboard + screen-reader
 * users get the selectable semantics that a `<Chip>` (a span) wouldn't carry.
 *
 * The optional `count` renders as a quiet right-aligned badge.
 */

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import styles from './FilterChip.module.css';

export interface FilterChipProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'type'
> {
  label: string;
  /** Optional right-aligned count badge. */
  count?: number;
  /** Selected state — flips background and color. */
  isActive: boolean;
}

export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(function FilterChip(
  { label, count, isActive, className, ...rest },
  ref,
) {
  const composedClassName = [styles.chip, isActive ? styles.chipActive : '', className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <button ref={ref} type="button" className={composedClassName} aria-pressed={isActive} {...rest}>
      <span>{label}</span>
      {count !== undefined ? <span className={styles.count}>{count}</span> : null}
    </button>
  );
});
